import { useState, useCallback, useEffect } from "preact/hooks";
import {
  getCartItems,
  addToCart as addToCartStorage,
  removeFromCart as removeFromCartStorage,
  setCartQuantity as setCartQuantityStorage,
  getCheckoutUrl as getCheckoutUrlStorage,
  type CartItem,
  type AddToCartMeta,
} from "@/lib/cartStorage";

export function useCart() {
  const [items, setItems] = useState<CartItem[]>(() => getCartItems());

  const syncFromStorage = useCallback(() => {
    setItems(getCartItems());
  }, []);

  useEffect(() => {
    syncFromStorage();
  }, [syncFromStorage]);

  const addToCart = useCallback(
    (variantId: number, quantity: number, meta?: AddToCartMeta) => {
      const next = addToCartStorage(variantId, quantity, meta);
      setItems(next);
      return next;
    },
    [],
  );

  const removeFromCart = useCallback((variantId: number) => {
    const next = removeFromCartStorage(variantId);
    setItems(next);
    return next;
  }, []);

  const setCartQuantity = useCallback((variantId: number, quantity: number) => {
    const next = setCartQuantityStorage(variantId, quantity);
    setItems(next);
    return next;
  }, []);

  const isInCart = useCallback(
    (variantId: number) => items.some((i) => i.variantId === variantId),
    [items],
  );

  const getQuantity = useCallback(
    (variantId: number) =>
      items.find((i) => i.variantId === variantId)?.quantity ?? 0,
    [items],
  );

  const toggleCart = useCallback(
    (variantId: number, quantity: number, meta?: AddToCartMeta) => {
      if (isInCart(variantId)) {
        removeFromCart(variantId);
        return false;
      }
      addToCart(variantId, quantity, meta);
      return true;
    },
    [isInCart, addToCart, removeFromCart],
  );

  const getCheckoutUrl = useCallback(
    (discountCode?: string | null) => getCheckoutUrlStorage(discountCode),
    [],
  );

  return {
    items,
    addToCart,
    removeFromCart,
    setCartQuantity,
    isInCart,
    getQuantity,
    toggleCart,
    getCheckoutUrl,
    syncFromStorage,
  };
}
